"use client";

import { useEffect, useId, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { AppRoutes } from "@/lib/routeResolver";
import { fetchActivePopupMessage, type PopupMessage } from "@/lib/popupMessage";
import styles from "./popup-message.module.css";

const SHOW_DELAY_MS = 700;

/** Module scope: survives client-side navigation, resets on a full reload. */
let shownThisLoad = false;

export default function PopupMessageDialog() {
  const pathname = usePathname();
  const [popup, setPopup] = useState<PopupMessage | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const pressedBackdrop = useRef(false);
  const titleId = useId();
  const bodyId = useId();

  useEffect(() => {
    fetchActivePopupMessage().then(setPopup);
  }, []);

  useEffect(() => {
    if (!popup || shownThisLoad) return;
    if (popup.homePage && pathname !== AppRoutes.home()) return;

    const timer = setTimeout(() => {
      shownThisLoad = true;
      dialogRef.current?.showModal();
      titleRef.current?.focus();
    }, SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [popup, pathname]);

  if (!popup) return null;

  const close = () => dialogRef.current?.close();

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      onPointerDown={(e) => {
        pressedBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (pressedBackdrop.current && e.target === e.currentTarget) close();
      }}
    >
      <div className={styles.ticket}>
        <div className={styles.paper}>
          <button
            type="button"
            className={styles.close}
            aria-label="Close"
            onClick={close}
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>

          <div className={styles.content}>
            <h2
              ref={titleRef}
              id={titleId}
              className={styles.title}
              tabIndex={-1}
            >
              {popup.title}
            </h2>
            <div
              id={bodyId}
              className={styles.body}
              dangerouslySetInnerHTML={{ __html: popup.description }}
            />
          </div>

          <div className={styles.stub}>
            <span className={styles.signature}>Central Holidays</span>
            <button type="button" className={styles.continue} onClick={close}>
              Continue browsing
            </button>
          </div>
        </div>
      </div>
    </dialog>
  );
}
